/**
 * mp4-meta-scrub.js — стереть метаданные mp4/mov, НЕ перекладывая файл.
 *
 * Зачем
 * -----
 * `ffmpeg -c copy` не кодирует ни байта и всё равно платит за файл трижды:
 * тянет его в кучу wasm, материализует там же вывод и копирует обратно в JS.
 * Замер на
 * /mute-video (память reference_wasm_copy_path_is_pure_io_2026_08_21, 902 МБ):
 * 28 с тёплым кэшем, 58 с холодным, +2.6 ГБ памяти — на телефоне это убитая
 * вкладка. Здесь замерено на /video-metadata: 570 МБ → 13.6 с только на снятие
 * GPS.
 *
 * Ключевое наблюдение: удаление метаданных mp4 — правка НЕСКОЛЬКИХ СОТЕН БАЙТ
 * внутри `moov`, и её можно сделать так, что длина `moov` не изменится ни на
 * байт. Тогда `mdat` не двигается, смещения чанков (`stco`/`co64`) остаются
 * верными, и трогать их не нужно вовсе. Выход собирается как Blob из ССЫЛОК
 * `file.slice()` плюс один пропатченный кусок — браузер держит их файловыми
 * хендлами, JS-куча не растёт.
 *
 * Как достигается неизменная длина
 * --------------------------------
 * Ненужная коробка не вырезается, а ПЕРЕИМЕНОВЫВАЕТСЯ в `free` (свободное
 * место) — это штатный ISO-BMFF механизм: размер тот же, содержимое обязаны
 * игнорировать все читатели. Даты в `mvhd`/`tkhd`/`mdhd` — поля фиксированной
 * длины, они просто обнуляются.
 *
 * ⛔ Модуль умеет только СТИРАТЬ. Записать новое значение тега в общем случае
 * нельзя без изменения длины — такие операции остаются за ffmpeg.
 *
 * API
 * ---
 *   MP4MetaScrub.looksSupported(file) -> bool
 *   MP4MetaScrub.scrub(file, ops) -> Promise<{blob, wiped:[...], bytesPatched}>
 *      ops: {gps, device, software, date, tags, all} — те же имена, что у
 *           пульта в /video-metadata.
 *
 * Бросает с машиночитаемой причиной, чтобы вызывающий откатился на ffmpeg:
 *   not-mp4-family, no-moov, moov-too-large, fragmented, nothing-to-do,
 *   short-read, bad-box.
 */
(function () {
'use strict';
if (window.MP4MetaScrub) return;

var MOOV_MAX = 96 * 1024 * 1024;
var TOP_GUARD = 4096;

function err(reason) { var e = new Error(reason); e.scrubReason = reason; return e; }
function fourcc(dv, o) { return String.fromCharCode(dv.getUint8(o), dv.getUint8(o+1), dv.getUint8(o+2), dv.getUint8(o+3)); }
function u64(dv, o) { return dv.getUint32(o) * 4294967296 + dv.getUint32(o + 4); }

function looksSupported(file) {
    var n = String((file && file.name) || '').toLowerCase();
    var t = String((file && file.type) || '').toLowerCase();
    return /\.(mp4|m4v|mov|m4a|3gp|3g2)$/.test(n) || t === 'video/mp4' || t === 'video/quicktime';
}

/* Прочитать кусок файла как ArrayBuffer. */
function slice(file, from, to) {
    return new Promise(function (resolve, reject) {
        var fr = new FileReader();
        fr.onload = function () { resolve(new Uint8Array(fr.result)); };
        fr.onerror = function () { reject(err('short-read')); };
        fr.readAsArrayBuffer(file.slice(from, to));
    });
}

/* Пройти коробки верхнего уровня, читая ТОЛЬКО заголовки (16 байт на коробку). */
async function topBoxes(file) {
    var out = [], pos = 0, guard = 0;
    while (pos < file.size && guard++ < TOP_GUARD) {
        var head = await slice(file, pos, Math.min(pos + 16, file.size));
        if (head.length < 8) break;
        var dv = new DataView(head.buffer, head.byteOffset, head.length);
        var size = dv.getUint32(0), type = fourcc(dv, 4), hdr = 8;
        if (size === 1) {
            if (head.length < 16) throw err('bad-box');
            size = u64(dv, 8); hdr = 16;
        } else if (size === 0) {
            size = file.size - pos;                 // «до конца файла»
        }
        if (size < hdr || pos + size > file.size) throw err('bad-box');
        out.push({ type: type, start: pos, size: size, hdr: hdr });
        pos += size;
    }
    return out;
}

/* Пройти коробки ВНУТРИ уже прочитанного буфера. Смещения — от начала буфера. */
function walk(buf, from, to) {
    var out = [], pos = from, guard = 0;
    var dv = new DataView(buf.buffer, buf.byteOffset, buf.length);
    while (pos + 8 <= to && guard++ < 65535) {
        var size = dv.getUint32(pos), type = fourcc(dv, pos + 4), hdr = 8;
        if (size === 1) { if (pos + 16 > to) break; size = u64(dv, pos + 8); hdr = 16; }
        else if (size === 0) size = to - pos;
        if (size < hdr || pos + size > to) break;
        out.push({ type: type, start: pos, size: size, hdr: hdr });
        pos += size;
    }
    return out;
}
function find(list, type) { for (var i = 0; i < list.length; i++) if (list[i].type === type) return list[i]; return null; }

/* `meta` в MP4 — FullBox (4 байта версии/флагов), в QuickTime — обычная
   коробка. Гадать по контейнеру нельзя: файлы бывают гибридные. Смотрим, что
   лежит сразу после заголовка: если это похоже на длину вложенной коробки —
   версии нет. */
function metaSkip(buf, box) {
    var dv = new DataView(buf.buffer, buf.byteOffset, buf.length);
    var at = box.start + box.hdr;
    if (at + 8 > box.start + box.size) return 4;
    var sz = dv.getUint32(at);
    return (sz >= 8 && sz <= box.size) ? 0 : 4;
}

/* Превратить коробку в `free`, не меняя длины: имя → 'free', тело → нули.
   Именно так ISO-BMFF помечает «здесь ничего нет»; все читатели обязаны это
   пропускать, а длина файла и все смещения остаются прежними. */
function toFree(buf, box, wiped, label) {
    buf[box.start + 4] = 0x66; buf[box.start + 5] = 0x72;   // 'f','r'
    buf[box.start + 6] = 0x65; buf[box.start + 7] = 0x65;   // 'e','e'
    buf.fill(0, box.start + box.hdr, box.start + box.size);
    wiped.push({ what: label || box.type, bytes: box.size });
}

/* Обнулить даты создания/изменения в mvhd/tkhd/mdhd. Поля фиксированной длины
   сразу после версии+флагов: v0 → два uint32, v1 → два uint64. */
function zeroDates(buf, box, wiped) {
    var o = box.start + box.hdr;
    var ver = buf[o];
    var n = (ver === 1) ? 16 : 8;
    if (o + 4 + n > box.start + box.size) return;
    buf.fill(0, o + 4, o + 4 + n);
    wiped.push({ what: box.type + '/date', bytes: n });
}

/* Ключи Apple-метаданных (`moov/meta/keys` + `ilst`) и классические
   `moov/udta/©xxx`. Здесь только то, что действительно раскрывает автора:
   остальное уносит режим «всё». */
/* ⚠️ Два семейства имён, и оба живые. MP4 из ffmpeg кладёт место в
   `udta/loci`, а QuickTime (и всё, что пишет камера Apple) — в `udta/©xyz`;
   у софта и устройства так же: `©swr`/`©mak`/`©mod` против Apple-ключей в
   `moov/meta`. Проверено дампом дерева на своих же файлах: в mp4 нашёлся
   только `loci`, в mov — только `©xyz`. Список, знающий одно семейство,
   молча оставляет координаты в файле. */
var GPS_ATOMS    = ['©xyz', 'loci'];
var DEVICE_ATOMS = ['©mak', '©mod'];
var SOFT_ATOMS   = ['©swr', '©too'];
var DATE_ATOMS   = ['©day'];
var TAG_ATOMS    = ['©nam', '©ART', '©alb', '©cmt', '©wrt', '©gen', '©dir',
                    'cprt', 'desc', 'ldes', 'aART', '©des', '©cpy', '©aut',
                    '©inf', '©prd', '©nrt', 'titl', 'auth'];
var DEVICE_KEYS = ['com.apple.quicktime.make', 'com.apple.quicktime.model'];
var SOFT_KEYS   = ['com.apple.quicktime.software'];
var GPS_KEYS    = ['com.apple.quicktime.location.ISO6709', 'com.apple.quicktime.location.accuracy.horizontal'];
var DATE_KEYS   = ['com.apple.quicktime.creationdate'];

/* `keys` перечисляет имена по порядку; i-й потомок `ilst` соответствует i-му
   имени. Возвращаем индексы (1-based, как их нумерует ilst), чьи имена нам
   интересны. */
function appleKeyIndexes(buf, keysBox, wanted) {
    var idx = [], dv = new DataView(buf.buffer, buf.byteOffset, buf.length);
    var o = keysBox.start + keysBox.hdr + 4;             // version+flags
    if (o + 4 > keysBox.start + keysBox.size) return idx;
    var count = dv.getUint32(o); o += 4;
    for (var i = 1; i <= count && o + 8 <= keysBox.start + keysBox.size; i++) {
        var sz = dv.getUint32(o);
        if (sz < 8) break;
        var name = '';
        try { name = new TextDecoder().decode(buf.subarray(o + 8, o + sz)); } catch (_) {}
        if (wanted.indexOf(name) >= 0) idx.push(i);
        o += sz;
    }
    return idx;
}

async function scrub(file, ops) {
    ops = ops || {};
    if (!looksSupported(file)) throw err('not-mp4-family');

    var tops = await topBoxes(file);
    if (find(tops, 'moof')) throw err('fragmented');
    var moov = find(tops, 'moov');
    if (!moov) throw err('no-moov');
    if (moov.size > MOOV_MAX) throw err('moov-too-large');

    var buf = await slice(file, moov.start, moov.start + moov.size);
    if (buf.length !== moov.size) throw err('short-read');

    var wiped = [];
    var kids = walk(buf, moov.hdr, buf.length);

    var wantAll = !!ops.all;
    var wantGps = wantAll || !!ops.gps;
    var wantDate = wantAll || !!ops.date;
    var wantDevice = wantAll || !!ops.device;
    var wantSoft = wantAll || !!ops.software;
    var wantTags = wantAll || !!ops.tags;

    // ── moov/udta ────────────────────────────────────────────────────────
    var udta = find(kids, 'udta');
    if (udta) {
        if (wantAll) {
            toFree(buf, udta, wiped, 'udta');
        } else {
            var uk = walk(buf, udta.start + udta.hdr, udta.start + udta.size);
            uk.forEach(function (b) {
                if (wantGps && GPS_ATOMS.indexOf(b.type) >= 0) return toFree(buf, b, wiped, 'gps:' + b.type);
                if (wantDate && DATE_ATOMS.indexOf(b.type) >= 0) return toFree(buf, b, wiped, 'date:' + b.type);
                if (wantDevice && DEVICE_ATOMS.indexOf(b.type) >= 0) return toFree(buf, b, wiped, 'device:' + b.type);
                if (wantSoft && SOFT_ATOMS.indexOf(b.type) >= 0) return toFree(buf, b, wiped, 'soft:' + b.type);
                if (wantTags && TAG_ATOMS.indexOf(b.type) >= 0) return toFree(buf, b, wiped, 'tag:' + b.type);
                if (b.type === 'meta') {
                    // Классический iTunes-стиль: udta/meta/ilst/<atom>
                    var mk = walk(buf, b.start + b.hdr + metaSkip(buf, b), b.start + b.size);
                    var ilst = find(mk, 'ilst');
                    if (!ilst) return;
                    walk(buf, ilst.start + ilst.hdr, ilst.start + ilst.size).forEach(function (it) {
                        if (wantGps && GPS_ATOMS.indexOf(it.type) >= 0) toFree(buf, it, wiped, 'gps:' + it.type);
                        else if (wantDate && DATE_ATOMS.indexOf(it.type) >= 0) toFree(buf, it, wiped, 'date:' + it.type);
                        else if (wantDevice && DEVICE_ATOMS.indexOf(it.type) >= 0) toFree(buf, it, wiped, 'device:' + it.type);
                        else if (wantSoft && SOFT_ATOMS.indexOf(it.type) >= 0) toFree(buf, it, wiped, 'soft:' + it.type);
                        else if (wantTags && TAG_ATOMS.indexOf(it.type) >= 0) toFree(buf, it, wiped, 'tag:' + it.type);
                    });
                }
            });
        }
    }

    // ── moov/meta (Apple keys: место, модель, программа, дата съёмки) ────
    var meta = find(kids, 'meta');
    if (meta) {
        if (wantAll) {
            toFree(buf, meta, wiped, 'meta');
        } else {
            var mkids = walk(buf, meta.start + meta.hdr + metaSkip(buf, meta), meta.start + meta.size);
            var keys = find(mkids, 'keys'), ilst2 = find(mkids, 'ilst');
            if (keys && ilst2) {
                var wanted = [];
                if (wantGps) wanted = wanted.concat(GPS_KEYS);
                if (wantDevice) wanted = wanted.concat(DEVICE_KEYS);
                if (wantSoft) wanted = wanted.concat(SOFT_KEYS);
                if (wantDate) wanted = wanted.concat(DATE_KEYS);
                var idx = appleKeyIndexes(buf, keys, wanted);
                if (idx.length) {
                    var items = walk(buf, ilst2.start + ilst2.hdr, ilst2.start + ilst2.size);
                    // Потомок ilst именуется НОМЕРОМ ключа (big-endian uint32).
                    items.forEach(function (it) {
                        var dv2 = new DataView(buf.buffer, buf.byteOffset, buf.length);
                        var n = dv2.getUint32(it.start + 4);
                        if (idx.indexOf(n) >= 0) toFree(buf, it, wiped, 'apple:' + n);
                    });
                }
            }
        }
    }

    // ── даты в заголовках ────────────────────────────────────────────────
    if (wantDate) {
        var mvhd = find(kids, 'mvhd');
        if (mvhd) zeroDates(buf, mvhd, wiped);
        kids.forEach(function (k) {
            if (k.type !== 'trak') return;
            var tk = walk(buf, k.start + k.hdr, k.start + k.size);
            var tkhd = find(tk, 'tkhd');
            if (tkhd) zeroDates(buf, tkhd, wiped);
            var mdia = find(tk, 'mdia');
            if (mdia) {
                var mdhd = find(walk(buf, mdia.start + mdia.hdr, mdia.start + mdia.size), 'mdhd');
                if (mdhd) zeroDates(buf, mdhd, wiped);
            }
        });
    }

    if (!wiped.length) throw err('nothing-to-do');

    var bytesPatched = wiped.reduce(function (s, w) { return s + w.bytes; }, 0);
    // Три части: до moov (ссылка на файл), пропатченный moov, после moov (ссылка).
    var parts = [];
    if (moov.start > 0) parts.push(file.slice(0, moov.start));
    parts.push(buf);
    if (moov.start + moov.size < file.size) parts.push(file.slice(moov.start + moov.size));

    return {
        blob: new Blob(parts, { type: file.type || 'video/mp4' }),
        wiped: wiped,
        bytesPatched: bytesPatched
    };
}

/* ══════════════════════════════════════════════════════════════════════
   ЧТЕНИЕ тегов из того же moov.

   Зачем отдельно от MediaInfo: MediaInfo протаскивает через FileReader ВЕСЬ
   файл. Замер на 199 МБ mp4 (медиана трёх прогонов, движок прогрет):
   MediaInfo 1114 мс против 28 мс у VideoMeta и 20 мс у прохода по moov —
   и разрыв растёт линейно с размером файла. При этом на первом экране от
   MediaInfo нужны РОВНО теги: всё остальное (длительность, GPS, дата,
   кодеки, разрешение, частота кадров) VideoMeta уже отдаёт из тех же
   заголовков.

   Поэтому теги читаем сами, а MediaInfo оставляем для глубокого паспорта
   (профиль, глубина цвета, число кадров, субтитры) — он лежит в свёрнутом
   аккордеоне и грузится, только когда его раскрывают.
   ══════════════════════════════════════════════════════════════════════ */

/* Четырёхбуквенный код iTunes-атома → ключ, которым его знает инструмент.
   Имена совпадают с ключами `-metadata` у ffmpeg, чтобы читатель и писатель
   говорили об одном и том же поле. */
var ILST_MAP = {
    '©nam': 'title', '©ART': 'artist', '©alb': 'album', 'aART': 'album_artist',
    '©wrt': 'composer', '©gen': 'genre', 'gnre': 'genre', '©day': 'date',
    '©cmt': 'comment', 'desc': 'description', 'ldes': 'synopsis',
    'cprt': 'copyright', '©cpy': 'copyright', '©dir': 'director',
    'tvsh': 'show', 'tvnn': 'network', '©too': 'encoder', '©swr': 'encoder',
    '©des': 'description', '©aut': 'artist', '©inf': 'comment', '©lyr': 'lyrics'
};
var KEY_MAP = {
    'com.apple.quicktime.title': 'title',
    'com.apple.quicktime.artist': 'artist',
    'com.apple.quicktime.author': 'artist',
    'com.apple.quicktime.description': 'description',
    'com.apple.quicktime.comment': 'comment',
    'com.apple.quicktime.copyright': 'copyright',
    'com.apple.quicktime.creationdate': 'date',
    'com.apple.quicktime.make': '_make',
    'com.apple.quicktime.model': '_model',
    'com.apple.quicktime.software': '_software'
};

function txt(buf, from, to) {
    if (to <= from) return '';
    try { return new TextDecoder('utf-8').decode(buf.subarray(from, to)).replace(/\u0000+$/, '').trim(); }
    catch (_) { return ''; }
}

/* Значение iTunes-атома лежит в дочерней коробке `data`: 1 байт версии,
   3 флага (тип данных), 4 байта локали, дальше сама строка. Тип 1 — UTF-8;
   числовые типы нам не нужны, у страницы все поля текстовые. */
function ilstValue(buf, item) {
    var kids = walk(buf, item.start + item.hdr, item.start + item.size);
    var d = find(kids, 'data');
    if (!d) return '';
    var dv = new DataView(buf.buffer, buf.byteOffset, buf.length);
    var type = dv.getUint32(d.start + d.hdr) & 0xffffff;
    var at = d.start + d.hdr + 8;
    if (type !== 1 && type !== 0) return '';
    return txt(buf, at, d.start + d.size);
}

/* QuickTime-атом `udta/©xxx`: 2 байта длины, 2 байта языка, дальше текст.
   Встречается и без этой шапки — тогда читаем всё тело. */
function qtValue(buf, box) {
    var dv = new DataView(buf.buffer, buf.byteOffset, buf.length);
    var at = box.start + box.hdr, end = box.start + box.size;
    if (end - at >= 4) {
        var len = dv.getUint16(at);
        if (len > 0 && at + 4 + len <= end) return txt(buf, at + 4, at + 4 + len);
    }
    return txt(buf, at, end);
}

function readTagsFrom(buf, kids, out) {
    var udta = find(kids, 'udta');
    if (udta) {
        walk(buf, udta.start + udta.hdr, udta.start + udta.size).forEach(function (b) {
            if (b.type === 'meta') {
                var mk = walk(buf, b.start + b.hdr + metaSkip(buf, b), b.start + b.size);
                var ilst = find(mk, 'ilst');
                if (ilst) {
                    walk(buf, ilst.start + ilst.hdr, ilst.start + ilst.size).forEach(function (it) {
                        var key = ILST_MAP[it.type];
                        if (!key || out.tags[key]) return;
                        var v = ilstValue(buf, it);
                        if (v) out.tags[key] = v;
                    });
                }
                return;
            }
            if (b.type === 'chpl') { out.chapters = Math.max(out.chapters, chplCount(buf, b)); return; }
            var k = ILST_MAP[b.type];
            if (k) {
                var v = qtValue(buf, b);
                if (v && !out.tags[k]) out.tags[k] = v;
                /* `©swr` — это ИМЕННО «программа», и детектор рисков смотрит на
                   неё отдельно от тега кодировщика: Adobe Premiere в этом поле
                   говорит об авторе, а Lavf — нет. */
                if (v && b.type === '©swr' && !out.device.software) out.device.software = v;
            }
        });
    }
    // Apple-ключи: moov/meta/keys + ilst, потомки ilst именуются НОМЕРОМ ключа.
    var meta = find(kids, 'meta');
    if (meta) {
        var mkids = walk(buf, meta.start + meta.hdr + metaSkip(buf, meta), meta.start + meta.size);
        var keys = find(mkids, 'keys'), ilst2 = find(mkids, 'ilst');
        if (keys && ilst2) {
            var names = appleKeyNames(buf, keys);
            var dv = new DataView(buf.buffer, buf.byteOffset, buf.length);
            walk(buf, ilst2.start + ilst2.hdr, ilst2.start + ilst2.size).forEach(function (it) {
                var idx = dv.getUint32(it.start + 4);
                var name = names[idx];
                var key = name && KEY_MAP[name];
                if (!key) return;
                var v = ilstValue(buf, it);
                if (!v) return;
                if (key === '_make') out.device.make = out.device.make || v;
                else if (key === '_model') out.device.model = out.device.model || v;
                else if (key === '_software') out.device.software = out.device.software || v;
                else if (!out.tags[key]) out.tags[key] = v;
            });
        }
    }
}

/* `chpl` — список глав Nero-стиля: версия+флаги, 1 байт резерва, затем
   количество. Формат гуляет между мультиплексорами, поэтому берём только
   счётчик и только когда он выглядит правдоподобно. */
function chplCount(buf, box) {
    var dv = new DataView(buf.buffer, buf.byteOffset, buf.length);
    var at = box.start + box.hdr;
    if (at + 9 > box.start + box.size) return 0;
    var n = dv.getUint8(at + 8);
    return (n > 0 && n < 512) ? n : 0;
}

function appleKeyNames(buf, keysBox) {
    var names = {}, dv = new DataView(buf.buffer, buf.byteOffset, buf.length);
    var o = keysBox.start + keysBox.hdr + 4;
    if (o + 4 > keysBox.start + keysBox.size) return names;
    var count = dv.getUint32(o); o += 4;
    for (var i = 1; i <= count && o + 8 <= keysBox.start + keysBox.size; i++) {
        var sz = dv.getUint32(o);
        if (sz < 8) break;
        names[i] = txt(buf, o + 8, o + sz);
        o += sz;
    }
    return names;
}

async function read(file) {
    if (!looksSupported(file)) throw err('not-mp4-family');
    var tops = await topBoxes(file);
    var moov = find(tops, 'moov');
    if (!moov) throw err('no-moov');
    if (moov.size > MOOV_MAX) throw err('moov-too-large');
    var buf = await slice(file, moov.start, moov.start + moov.size);
    var out = { tags: {}, device: { make: '', model: '', software: '' }, chapters: 0 };
    readTagsFrom(buf, walk(buf, moov.hdr, buf.length), out);
    return out;
}

window.MP4MetaScrub = { looksSupported: looksSupported, scrub: scrub, read: read };
})();
